import React                                                    from "react";
import { useDispatch }                                          from "react-redux";
import { ReactFlow, 
         Node, 
         Edge,
         Background, 
         Controls, 
         applyEdgeChanges, 
         applyNodeChanges,
         NodeChange,
         EdgeChange,
         Panel,
       }                                                        from '@xyflow/react';
import { Box,  
         Card, 
         CardContent, 
         CircularProgress, 
         Typography,
       }                                                        from "@mui/material";
import CustomNode, 
       { ServiceNodeData }                                      from "./CustomNode";
import { useSelector }                                          from "react-redux";
import { addEdge, 
         addSystemNodeAndPosition, 
         onDirectEdgesChange, 
         onDirectNodesChange, 
         removeEdge, 
         updateNodePosition 
       }                                                        from "store/dataReducers/workflowSlice";
import { BglPrimarySmallButton }                                from "components/button/BglButtons";
import AddIcon                                                  from '@mui/icons-material/Add';
import { useState,
         useEffect
       }                                                        from "react";
import { useGlobalConfig }                                      from "context/GlobalContext";
import { ANALYTICS_EVENTS }                                     from "framework/analytics/AnalyticsEvents";
import { WorkflowAPI }                                          from "apis/workflowAPI";
import { CreateNewNode }                                        from "./CreateNewNode";
import { GetValues }                                            from "types/cms/frontendTypes";
import '@xyflow/react/dist/style.css'

const nodeTypes = {
  custom: CustomNode
}

export const WorkflowComponent = (props : { workflowDocId : string }) => {

  const { rc } = useGlobalConfig()

  const [isAddNodeDialogOpen, setIsAddNodeDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const nodes = useSelector((state : any) => state.workflow.nodes)
  const edges = useSelector((state : any) => state.workflow.edges)
  const workflow = useSelector((state : any) => state.workflow.workflows.find(
      (workflow : any) => workflow.documentId === props.workflowDocId))
  
  const dispatch = useDispatch();

  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.EDITOR_OPENED)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onNodesChange = async (changes : NodeChange<any>[]) => {
    dispatch(onDirectNodesChange(applyNodeChanges(changes, nodes)))
  }
  
  const onEdgesChange = async (changes: EdgeChange<any>[]) => {

    switch (changes[0].type) {
      case 'add':
        break;
      case 'replace':
        break;
      case 'remove':
        const deletedEdgeResp = await WorkflowAPI.deleteSystemNodeEdge(rc, changes[0].id)
        if (deletedEdgeResp && deletedEdgeResp.error) {
          console.log(`Error`, deletedEdgeResp.error);
          return
        }
        dispatch(removeEdge(changes[0].id))
        rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.CONNECTION_DELETED)
        break;
      case 'select':
        break;
      default:
    }
    
    dispatch(onDirectEdgesChange(applyEdgeChanges(changes, edges)))
  }

  const onConnect = async (connection : any) => {
    
    const edge : Edge = { 
      id : '', // Will get set by the backend
      type : 'step',
      source : connection.source,
      target : connection.target,
      sourceHandle : connection.sourceHandle,
      targetHandle : connection.targetHandle,
      label : '',
    }

    const newEdge = await WorkflowAPI.createSystemNodeEdge(rc, edge, workflow.documentId)
    if (newEdge.error) {
      console.log(`Error`, newEdge.error);
      return
    }

    dispatch(addEdge(newEdge.data!));
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.CONNECTION_CREATED)
  }

  const onNodeDragStop = async (event : any, node : Node<ServiceNodeData>) => {
    
    const nodePosition : GetValues<"api::system-node-position.system-node-position"> = {
      positionX : Math.round(node.position.x),
      positionY : Math.round(node.position.y),
    }

    const updatedNode = await WorkflowAPI.updateNodePosition(rc, node.data.positionDocId, nodePosition)
    if (updatedNode.error) {
      console.log(`Error`, updatedNode.error);
      return
    }

    dispatch(updateNodePosition(updatedNode.data!))
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.NODE_DRAGGED, { node_type : node.data.type })
  }

  const selectExistingNode = (node : Node<ServiceNodeData>) => {
    console.log(node)
  }

  const addNewNode = async (newNodeData : ServiceNodeData) => {

    const nodeData : Node<ServiceNodeData> = { 
      id : '', // Will get set by the backend
      type : 'custom',
      position : {
        x : 0,
        y : 0
      },
      data : {
        label : newNodeData.label,
        description : newNodeData.description,
        type : newNodeData.type,
        configs : [],
        positionDocId : '' // Will get set by the backend
      }
    }

    const newNode = await WorkflowAPI.createSystemNode(rc, workflow.documentId, nodeData)
    if (newNode.error) {
      console.log(`Error`, newNode.error);
      return
    }

    const sysNode = newNode.data!.data
    const sysNodePos = newNode.data!.nodePosition
    dispatch(addSystemNodeAndPosition({systemNode : sysNode, nodePosition : sysNodePos}));
    rc.analytics.trackEvent(ANALYTICS_EVENTS.WORKFLOW.NODE_ADDED, { node_type : newNodeData.type })

    setIsAddNodeDialogOpen(false);
    showMessage('Node added successfully', 'success');    
  }

  const showMessage = (message: string, severity: 'success' | 'error') => {
    console.log(message, severity);
  }
    
  return (
    <Box sx={{ width: '100%', height: '100%' }}>

      {
        loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        )
      }

      {
        !loading && (
          <React.Fragment>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
              <BglPrimarySmallButton 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setIsAddNodeDialogOpen(true)}>
                  Add Node
              </BglPrimarySmallButton>
            </Box>

            <Card>
              <CardContent sx={{ height: `calc(100vh - 100px)`, padding: 0 }}>
                <ReactFlow 
                  nodes={nodes || []} 
                  edges={edges || []}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onNodeDragStop={onNodeDragStop}
                  nodeTypes={nodeTypes}
                  fitView>
                  <Background />
                  <Controls />
                  <Panel position="top-left">
                    <Typography variant="bglBody2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Data Sources (Utility Input)
                    </Typography>
                  </Panel>
                  <Panel position="top-right">
                    <Typography variant="bglBody2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Products
                    </Typography>
                  </Panel>
                  <Panel position="bottom-left">
                    <Typography variant="bglBody2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Data Sources (Output)
                    </Typography>
                  </Panel>
                </ReactFlow>
              </CardContent>
            </Card>
          </React.Fragment>   
        )
      }

      {/* Add Node Dialog */}
      <CreateNewNode 
        isAddNodeDialogOpen={isAddNodeDialogOpen}
        setIsAddNodeDialogOpen={setIsAddNodeDialogOpen}
        selectExistingNode={selectExistingNode}
        addNewNode={addNewNode}
      />
      
    </Box>
  )
};


